import React, { useEffect, useRef, useState, useCallback } from "react";

import {
  Button,
  Card,
  Col,
  Container,
  Form,
  Nav,
  Row,
  Collapse,
} from "react-bootstrap";

import { ChevronDown, ChevronRight } from "react-bootstrap-icons";

import StickyBox from "react-sticky-box";

import { WarningCard, LoadingSpinner } from "../../components";

import "./GNSSPerformanceReport.css";

import { COLORS } from "utils/colors";

import { DatePickerEdit, SelectEdit } from "components";
import { useLocation } from "react-router-dom";
import { useGnssApi } from "utils/hooks";
import { useFilter } from "utils/hooks";
import { InterferenceAnalysisCard } from "../../components/cards/InterferenceAnalysisCard";

const Sidebar = React.memo(
  ({
    width,
    title,
    initialDate,
    dateDisabled,
    stations,
    selectedStation,
    activeKey,
    alerts,
    onStationChanged,
    onDateChanged,
    onCustomInterval,
    onAlertChanged,
  }) => {
    const ALERT_TYPES = {
      INTERFERENCE: "Interference alerts",
      SPOOFING: "Spoofing alerts",
      SPOOFING_AND_INTERFERENCE: "Spoofing and interference alerts",
    };

    const SEVERITY_ORDER = {
      CRITICAL: 0,
      WARNING: 1,
      INFO: 2,
    };

    const [startDate, setStartDate] = useState(initialDate);

    const [openSections, setOpenSections] = useState({
      INTERFERENCE: false,
      SPOOFING: false,
      SPOOFING_AND_INTERFERENCE: false,
    });

    const toggleSection = (type) => {
      setOpenSections((prev) => ({
        ...prev,
        [type]: !prev[type],
      }));
    };

    const groupedAlerts = alerts.reduce((acc, alert) => {
      if (!acc[alert.eventType]) {
        acc[alert.eventType] = [];
      }

      acc[alert.eventType].push(alert);

      acc[alert.eventType].sort(
        (a, b) =>
          (SEVERITY_ORDER[a.severity] ?? 999) -
          (SEVERITY_ORDER[b.severity] ?? 999),
      );

      return acc;
    }, {});

    const timeUtcDisplay = (timestamp) => {
      const date = new Date(timestamp);

      const time =
        `${date.getUTCHours().toString().padStart(2, "0")}:` +
        `${date.getUTCMinutes().toString().padStart(2, "0")}:` +
        `${date.getUTCSeconds().toString().padStart(2, "0")}`;

      return time;
    };

    return (
      <Nav
        activeKey={"link-" + activeKey}
        style={{
          overflowY: "auto",
          overflowX: "clip",
          height: "calc(100vh - 56px)",
          width: width,
          borderLeft: "1px solid #4e4d4d",
        }}
        variant="pills"
        md={12}
        className="d-none d-md-block sidebarNav sidebar"
      >
        <div key={"nav-title"} className="pt-3 ">
          <Col md={{ span: 8, offset: 2 }}>
            <h6 className="text-center" style={{ color: "white" }}>
              {title}
            </h6>
          </Col>
        </div>
        <div key={"nav-date"} className="mt-3 ">
          <Col key={"report-form-col"} md={{ span: 10, offset: 1 }}>
            <SelectEdit
              label={"Change station"}
              onChange={(e) => {
                onStationChanged(e.target.value);
              }}
            >
              {stations ? (
                stations
                  .sort((a, b) => {
                    // Move item with `property === value` to front
                    if (a.identifier === selectedStation.identifier) return -1;
                    if (b.identifier === selectedStation.identifier) return 1;
                    return 0;
                  })
                  .map((station, index) => {
                    return (
                      <option key={index} value={station.identifier}>
                        {station.name}
                      </option>
                    );
                  })
              ) : (
                <></>
              )}
            </SelectEdit>
            <DatePickerEdit
              label={"Change alerts date"}
              startDate={startDate}
              disabled={dateDisabled}
              onChange={(date) => {
                setStartDate(date);
                onDateChanged(date);
              }}
            />
          </Col>
        </div>
        <hr key={"divider"} />
        <Nav.Item key={"nav-item-1"}>
          <Nav.Link
            key={`link-custom`}
            eventKey={`link-custom`}
            onClick={() => {
              onCustomInterval("custom");
            }}
          >
            <strong>Custom interval</strong>
          </Nav.Link>
          <Nav.Link
            key={`link-realtime`}
            eventKey={`link-realtime`}
            onClick={() => {
              onCustomInterval("realtime");
            }}
          >
            <strong>Realtime analytics</strong>
          </Nav.Link>
          {Object.entries(ALERT_TYPES).map(([type, label]) => {
            const typeAlerts = groupedAlerts[type];

            if (!typeAlerts?.length) {
              return null;
            }

            const isAlertPreset = typeAlerts.find(
              (typedAlert) => typedAlert.id === activeKey,
            );

            const isOpen = isAlertPreset ? true : openSections[type];

            return (
              <React.Fragment key={type}>
                <Nav.Link
                  onClick={() => toggleSection(type)}
                  className="d-flex align-items-center justify-content-between"
                >
                  <strong>{label}</strong>

                  {isOpen ? (
                    <ChevronDown size={14} />
                  ) : (
                    <ChevronRight size={14} />
                  )}
                </Nav.Link>

                <Collapse in={isOpen}>
                  <div>
                    {typeAlerts.map((alert, index) => (
                      <Nav.Link
                        key={alert.id}
                        eventKey={`link-${alert.id}`}
                        className="ps-4 link-danger"
                        onClick={() => onAlertChanged(index, alert.id)}
                      >
                        <strong>
                          {" "}
                          {timeUtcDisplay(alert.startTime)} ({alert.severity})
                        </strong>
                      </Nav.Link>
                    ))}
                  </div>
                </Collapse>
              </React.Fragment>
            );
          })}
        </Nav.Item>
      </Nav>
    );
  },
);

export const GNSSInterferenceReport = ({ route, navigation }) => {
  const api = useGnssApi();
  const refs = useRef([]);
  const myRef = React.createRef();
  const { groupFilter } = useFilter();

  const [alerts, setAlerts] = useState([]);
  const [selectedAlertId, setSelectedAlertId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [stopDate, setStopDate] = useState(null);
  const [warning, setWarning] = useState({
    title: "",
    message: "",
    blocking: false,
  });

  const [currentIndex, setCurrentIndex] = useState("custom");
  const [mode, setMode] = useState("custom");
  const [stationsDetails, setStationsDetails] = useState();
  const [selectedStation, setSelectedStation] = useState("");
  const [initialDate, setInitialDate] = useState(new Date());

  const location = useLocation();

  const alertFilter = {
    state: "STOPPED",
    fields: ["id", "state", "severity", "eventType", "startTime"],
  };

  useEffect(() => {
    if (!stationsDetails?.length) return;

    const identifier = location.state?.stationIdentifier;

    const nextStation = identifier
      ? stationsDetails.find((s) => s.identifier === identifier)
      : stationsDetails[0];

    if (nextStation && nextStation.identifier !== selectedStation?.identifier) {
      setSelectedStation(nextStation);
    }
  }, [stationsDetails]);

  useEffect(() => {
    getAllStationsDetails();
  }, []);

  useEffect(() => {
    if (selectedStation) {
      getLastAlerts();
    }
  }, [selectedStation]);

  const handleStationChanged = (stationId) => {
    setAlerts([]);
    setCurrentIndex("custom");
    setSelectedAlertId(null);
    setSelectedStation(
      stationsDetails.find((station) => {
        return station.identifier === stationId;
      }),
    );
  };

  const handleDateChanged = (date) => {
    const utcDate = date.toDate();
    const offsetHours = -date.toDate().getTimezoneOffset() / 60;
    utcDate.setHours(offsetHours, 0, 0, 0);

    setInitialDate(date);

    getAlertsByDate(utcDate.toISOString().split("T")[0]);
  };

  const handleAlertChanged = (index, alertId) => {
    setCurrentIndex(alertId);
    setSelectedAlertId(alertId);
  };

  const handleCustomInterval = (index) => {
    setCurrentIndex(index);

    setMode(index);
    setSelectedAlertId(index);
  };

  const getAllStationsDetails = async () => {
    try {
      setIsLoading(true);
      let response = await api.getAllStationsDetails();
      response = response.data;
      if (!response || response.length === 0) {
        setIsLoading(false);
        setIsSuccess(false);
        setWarning({
          title: "No stations available",
          message: `Your administrator hasn’t granted you access to the stations. Please contact your administrator to request the required permissions.  
  If you are the administrator, please ensure that your account has the necessary permissions and that at least one network and receiver are configured in the platform settings.`,
          blocking: true,
        });

        return;
      }

      setIsLoading(true);
      response = response.filter((station) =>
        station.groupNames.some(
          (groupName) => !groupFilter.includes(groupName),
        ),
      );

      setStationsDetails(response);

      setIsLoading(false);
      return response;
    } catch (e) {
      setIsLoading(false);
      //console.error("Failed to fetch stations", e);

      setIsSuccess(false);
      setWarning({
        title: "No stations available",
        message: `Your administrator hasn't granted you access to the receiver. If you are the administrator please check that you have networks and stations added in platform  `,

        blocking: true,
      });
    }
  };

  const getAlertsByDate = async (date) => {
    try {
      setIsLoading(true);
      const response = await api.getAlertsByStationIdFiltered(
        selectedStation.groupNames[0],
        selectedStation.identifier,
        { ...alertFilter, date: date },
      );

      if (
        (response?.status &&
          (response?.status !== "SUCCESS" || response?.code !== 200)) ||
        !response.data
      ) {
        setIsSuccess(false);
        setIsLoading(false);
        setWarning({
          title: "No alerts generated for this station",
          message: `The station with identifier ${selectedStation.identifier} didn't generate any alerts. Please choose another station.`,
          blocking: false,
        });

        return;
      }
      setIsLoading(false);
      setIsSuccess(true);

      setAlerts(response?.data);

      setSelectedAlertId("custom");
      setCurrentIndex("custom");
      setMode("custom");
      //setSelectedAlertId(response?.data.alertIds[0].id);

      return response;
    } catch (e) {
      setIsLoading(false);
      setIsSuccess(false);
      //throw e;
    }
  };

  const getLastAlerts = async () => {
    try {
      setIsLoading(true);
      let response = await api.getAlertsByStationIdFiltered(
        selectedStation.groupNames[0],
        selectedStation.identifier,
        alertFilter,
      );

      setIsLoading(false);
      setIsSuccess(true);

      setAlerts(response?.data);
      setInitialDate(new Date(response?.data[0]?.startTime));

      if (location.state?.alertState === "FIRING") {
        setSelectedAlertId("realtime");
        setCurrentIndex("realtime");
        setMode("realtime");
        return response;
      }

      if (location.state?.alertState === "STOPPED" && location.state?.alertId) {
        setSelectedAlertId(location.state?.alertId);
        setCurrentIndex(
          response?.data?.findIndex(
            (alert) => alert.id === location.state?.alertId,
          ),
        );
      } else {
        setSelectedAlertId("custom");
        setCurrentIndex("custom");
        setMode("custom");
      }

      return response;
    } catch (e) {
      setIsLoading(false);
      setIsSuccess(false);
    }
  };
  if (isLoading) return <LoadingSpinner />;

  // if (!isSuccess && !isLoading && warning.blocking)
  //   return <WarningCard {...warning} />;

  return (
    <>
      <div
        style={{
          background: COLORS.black,
          // width: "calc(100vw - 250px)",
          display: "flex",
          alignItems: "flex-start",
          marginTop: 0,
          position: "relative",
          height: "calc(100vh - 56px)",
          overflow: "auto",
        }}
      >
        <StickyBox>
          <Sidebar
            activeKey={selectedAlertId}
            title={selectedStation.name}
            selectedStation={selectedStation}
            width={300}
            alerts={alerts}
            onCustomInterval={(index) => handleCustomInterval(index)}
            onAlertChanged={(index, title) => handleAlertChanged(index, title)}
            onStationChanged={(stationId) => handleStationChanged(stationId)}
            initialDate={initialDate}
            onDateChanged={(date) => handleDateChanged(date)}
            stations={stationsDetails ? stationsDetails : []}
          />
        </StickyBox>

        <div
          className="reportContent"
          style={{
            position: "relative",
            padding: 0,
            width: "calc(100% - 300px)",
          }}
        >
          {/* {!isSuccess && !isLoading && !warning.blocking && (
            <WarningCard {...warning} />
          )} */}
          {isSuccess && !isLoading && (
            <>
              <div className="align-items-end m-3">
                <InterferenceAnalysisCard
                  key={selectedAlertId}
                  groupName={selectedStation?.groupNames[0]}
                  stationId={selectedStation?.identifier}
                  alertId={selectedAlertId}
                  mode={mode}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};
